<template>
  <div>
    <UiToast
      :show="toast.show"
      :message="toast.message"
      :type="toast.type"
      @close="toast.show = false"
    />

    <!-- modals (props/handlers preserved) -->
    <CustomerModal
      :showModal="showCustomerModal"
      :customersList="customersList"
      @close="closeCustomerModal"
      @created="handleUserCreated"
    />
    <GroupModal
      :showModal="showGroupModal"
      :customersList="customersList"
      @close="closeGroupModal"
      @save="saveCustomer"
    />
    <PortalAdminModal
      :showModal="showPortalAdminModal"
      @close="showPortalAdminModal = false"
      @created="handlePortalAdminCreated"
    />
    <UserEditModal
      ref="editModalRef"
      :showModal="showEditModal"
      :user="selectedUser"
      @close="closeEditModal"
      @save="handleUpdateProfile"
    />
    <UserAssignmentModal
      ref="assignmentModalRef"
      :showModal="showAssignmentModal"
      :user="selectedUser"
      :availableCustomers="customersList"
      :availableGroups="groupsList"
      @close="closeAssignmentModal"
      @saveCustomers="handleUpdateCustomers"
      @saveGroups="handleUpdateGroups"
      @saved="handleMcsAssignmentsSaved"
      @fetchGroups="fetchGroupsForSite"
    />
    <ConfirmationModal
      :show="showDeleteConfirm"
      title="Delete User"
      :message="`Are you sure you want to delete ${selectedUser?.first_name} ${selectedUser?.last_name}? This action cannot be undone.`"
      confirmText="Delete"
      type="danger"
      :loading="actionLoading"
      @confirm="handleDeleteUser"
      @cancel="closeDeleteConfirm"
    />
    <ConfirmationModal
      :show="showResetConfirm"
      title="Reset Password"
      :message="`Send a password reset email to ${selectedUser?.email}? The user will receive a new password via email.`"
      confirmText="Reset Password"
      type="warning"
      :loading="actionLoading"
      @confirm="handleResetPassword"
      @cancel="closeResetConfirm"
    />

    <UiPageHeader
      title="Customer Accounts"
      subtitle="Users, their assigned customers or groups, and role management"
      breadcrumb="accounts / users"
      @toggle-nav="toggleNav"
    >
      <template #actions>
        <UiButton variant="secondary" :left-icon="Plus" @click="addGroup">Add Group</UiButton>
        <UiButton variant="secondary" :left-icon="ShieldCheck" @click="showPortalAdminModal = true">Add Portal Admin</UiButton>
        <UiButton variant="primary" :left-icon="Plus" @click="addCustomer">Add Customer</UiButton>
      </template>
    </UiPageHeader>

    <UiCard>
      <template #header>
        <h2 class="text-sm font-semibold text-text">Accounts</h2>
        <span class="font-mono text-[11px] text-text-3">
          {{ filteredUsers.length }} of {{ users.length }}
        </span>
      </template>
      <template #actions>
        <div class="w-72">
          <UiInput
            v-model="searchQuery"
            placeholder="Search name or email…"
            :left-icon="Search"
            clearable
          />
        </div>
        <div class="w-52">
          <UiSelect v-model="accountTypeFilter">
            <option value="">All account types</option>
            <option value="CUSTOMER">Customer (MCS)</option>
            <option value="SUPPLIER">Supplier</option>
          </UiSelect>
        </div>
      </template>

      <UiTable
        :columns="[
          { key: 'name',         label: 'Name',         sortable: true },
          { key: 'email',        label: 'Email',        sortable: true },
          { key: 'role',         label: 'Role',         sortable: true },
          { key: 'assigned',     label: 'Assigned' },
          { key: 'account_type', label: 'Account Type', sortable: true },
          { key: 'actions',      label: 'Actions',      width: 'w-[140px]' },
        ]"
        :sort-key="sortConfig.field"
        :sort-dir="sortConfig.direction"
        @sort="sortBy"
      >
        <tr v-for="user in filteredUsers" :key="user.id">
          <td>
            <span class="text-text">{{ user.first_name }} {{ user.last_name }}</span>
          </td>
          <td class="muted">{{ user.email }}</td>
          <td>
            <UiBadge :tone="user.role === 'PORTAL_SUPER_ADMIN' ? 'warn' : 'lime'">
              {{ roleLabel(user.role) }}
            </UiBadge>
          </td>
          <td>
            <div v-if="user.role === 'PORTAL_SUPER_ADMIN'">
              <span class="text-[12px] text-text-2 italic">All customers (portal super admin)</span>
            </div>
            <div v-else-if="user.account_type === 'CUSTOMER'" class="space-y-0.5">
              <div
                v-for="c in user.customers_mcs"
                :key="c.mcs_customer_id"
                class="text-[12px] text-text-2"
              >
                {{ c.customer_name }}
                <span class="font-mono text-[10.5px] text-text-3">({{ c.site }})</span>
              </div>
              <span
                v-if="!user.customers_mcs || user.customers_mcs.length === 0"
                class="text-[12px] text-text-4 italic"
              >No customers assigned</span>
            </div>
            <div v-else-if="user.account_type === 'SUPPLIER'" class="space-y-0.5">
              <div
                v-for="g in user.group"
                :key="g.group_id"
                class="text-[12px] text-text-2"
              >
                {{ g.group_name }}
                <span class="font-mono text-[10.5px] text-text-3">({{ g.site_name }})</span>
              </div>
              <span
                v-if="!user.group || user.group.length === 0"
                class="text-[12px] text-text-4 italic"
              >No groups assigned</span>
            </div>
          </td>
          <td>
            <UiStatusPill
              :status="user.account_type === 'CUSTOMER' ? 'ok' : 'pending'"
            >{{ user.account_type || 'N/A' }}</UiStatusPill>
          </td>
          <td>
            <div class="flex items-center gap-0.5">
              <button
                @click="openEditModal(user)"
                class="p-2 text-text-2 hover:text-lime hover:bg-bg-2 rounded transition-colors"
                title="Edit profile"
                aria-label="Edit profile"
              ><Pencil :size="16" :stroke-width="1.75" /></button>
              <button
                v-if="user.role !== 'PORTAL_SUPER_ADMIN'"
                @click="openAssignmentModal(user)"
                class="p-2 text-text-2 hover:text-lime hover:bg-bg-2 rounded transition-colors"
                :title="user.account_type === 'CUSTOMER' ? 'Manage customers' : 'Manage groups'"
                :aria-label="user.account_type === 'CUSTOMER' ? 'Manage customers' : 'Manage groups'"
              ><UsersRound :size="16" :stroke-width="1.75" /></button>
              <button
                @click="openResetConfirm(user)"
                class="p-2 text-text-2 hover:text-warn hover:bg-bg-2 rounded transition-colors"
                title="Reset password"
                aria-label="Reset password"
              ><KeyRound :size="16" :stroke-width="1.75" /></button>
              <button
                @click="openDeleteConfirm(user)"
                class="p-2 text-text-2 hover:text-err hover:bg-bg-2 rounded transition-colors"
                title="Delete user"
                aria-label="Delete user"
              ><Trash2 :size="16" :stroke-width="1.75" /></button>
            </div>
          </td>
        </tr>
      </UiTable>

      <UiEmpty
        v-if="filteredUsers.length === 0"
        :icon="searchQuery || accountTypeFilter ? SearchX : Users"
        :title="searchQuery || accountTypeFilter ? 'No matching users' : 'No customer accounts yet'"
        :hint="searchQuery || accountTypeFilter ? 'Adjust search or filter.' : 'Use \'Add Customer\' to create one.'"
      />
    </UiCard>
  </div>
</template>

<script>
import { onUnmounted, ref, computed, reactive } from "vue";
import CustomerModal from "@/components/CustomerModal.vue";
import GroupModal from "@/components/GroupModal.vue";
import PortalAdminModal from "@/components/PortalAdminModal.vue";
import UserEditModal from "@/components/UserEditModal.vue";
import UserAssignmentModal from "@/components/UserAssignmentModal.vue";
import ConfirmationModal from "@/components/ConfirmationModal.vue";
import UiPageHeader from "@/components/ui/UiPageHeader.vue";
import UiCard from "@/components/ui/UiCard.vue";
import UiButton from "@/components/ui/UiButton.vue";
import UiInput from "@/components/ui/UiInput.vue";
import UiSelect from "@/components/ui/UiSelect.vue";
import UiBadge from "@/components/ui/UiBadge.vue";
import UiStatusPill from "@/components/ui/UiStatusPill.vue";
import UiTable from "@/components/ui/UiTable.vue";
import UiEmpty from "@/components/ui/UiEmpty.vue";
import UiToast from "@/components/ui/UiToast.vue";
import {
  Plus, Search, Pencil, UsersRound, KeyRound, Trash2, Users, SearchX, ShieldCheck,
} from "lucide-vue-next";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "@/firebase/config";
import { useStore } from "vuex";
import { AdminApiService } from "@/services/adminApi";

export default {
  components: {
    CustomerModal,
    GroupModal,
    PortalAdminModal,
    UserEditModal,
    UserAssignmentModal,
    ConfirmationModal,
    UiPageHeader,
    UiCard,
    UiButton,
    UiInput,
    UiSelect,
    UiBadge,
    UiStatusPill,
    UiTable,
    UiEmpty,
    UiToast,
    // Lucide icons used as template tags (<Pencil />, <Trash2 />, ...).
    // Options API does NOT auto-register setup()-returned values as components,
    // so these must be listed explicitly or the tags render as nothing.
    Plus, Search, Pencil, UsersRound, KeyRound, Trash2, Users, SearchX, ShieldCheck,
  },
  props: {
    toggleNav: { type: Function, default: () => {} },
  },
  setup() {
    const users = ref([]);
    const showCustomerModal = ref(false);
    const showGroupModal = ref(false);
    const showPortalAdminModal = ref(false);
    const customersList = ref([]);
    const groupsList = ref([]);
    let colRef = collection(db, "Users");
    const store = useStore();

    // Initialize API Service
    const adminApi = new AdminApiService(store.state.apiUrl);

    // Search and Filter State
    const searchQuery = ref("");
    const accountTypeFilter = ref("");
    const sortConfig = ref({ field: "name", direction: "asc" });

    // Modal States
    const showEditModal = ref(false);
    const showAssignmentModal = ref(false);
    const showDeleteConfirm = ref(false);
    const showResetConfirm = ref(false);
    const selectedUser = ref(null);
    const actionLoading = ref(false);

    // Modal Refs
    const editModalRef = ref(null);
    const assignmentModalRef = ref(null);

    // Toast Notification
    const toast = reactive({
      show: false,
      message: "",
      type: "success"
    });

    const showToast = (message, type = "success") => {
      toast.message = message;
      toast.type = type;
      toast.show = true;
      setTimeout(() => {
        toast.show = false;
      }, 4000);
    };

    // Firestore Real-time Listener
    let unsubscribe = null;

    unsubscribe = onSnapshot(colRef, (snapshot) => {
      let results = [];

      snapshot.docs.forEach((doc) => {
        if (doc.data().role != "SUPER_ADMIN") {
          results.push({ ...doc.data(), id: doc.id });
        }
      });

      users.value = results;
    });

    onUnmounted(() => {
      if (unsubscribe) {
        unsubscribe();
      }
    });

    // Computed: Filtered and Sorted Users
    const filteredUsers = computed(() => {
      let result = [...users.value];

      // Apply search filter
      if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase();
        result = result.filter(
          (user) =>
            `${user.first_name} ${user.last_name}`.toLowerCase().includes(query) ||
            user.email?.toLowerCase().includes(query)
        );
      }

      // Apply account type filter
      if (accountTypeFilter.value) {
        result = result.filter(
          (user) => user.account_type === accountTypeFilter.value
        );
      }

      // Apply sorting
      const { field, direction } = sortConfig.value;
      result.sort((a, b) => {
        let aVal, bVal;

        if (field === "name") {
          aVal = `${a.first_name || ""} ${a.last_name || ""}`.toLowerCase();
          bVal = `${b.first_name || ""} ${b.last_name || ""}`.toLowerCase();
        } else {
          aVal = (a[field] || "").toString().toLowerCase();
          bVal = (b[field] || "").toString().toLowerCase();
        }

        const comparison = aVal.localeCompare(bVal);
        return direction === "asc" ? comparison : -comparison;
      });

      return result;
    });

    // Friendly display label for a role value; PORTAL_SUPER_ADMIN gets a
    // human-readable label, all other roles keep the raw uppercase form.
    const roleLabel = (role) => {
      if (role === "PORTAL_SUPER_ADMIN") return "Portal Super Admin";
      return (role || "").replaceAll("_", " ");
    };

    // Sorting Functions
    const sortBy = (field) => {
      if (sortConfig.value.field === field) {
        // Toggle direction
        sortConfig.value.direction =
          sortConfig.value.direction === "asc" ? "desc" : "asc";
      } else {
        sortConfig.value.field = field;
        sortConfig.value.direction = "asc";
      }
    };

    // Modal Functions - Existing
    const addCustomer = () => {
      showCustomerModal.value = true;
    };

    const addGroup = () => {
      showGroupModal.value = true;
    };

    const closeCustomerModal = () => {
      showCustomerModal.value = false;
    };

    const closeGroupModal = () => {
      showGroupModal.value = false;
    };

    // Modal Functions - Edit
    const openEditModal = (user) => {
      selectedUser.value = user;
      showEditModal.value = true;
    };

    const closeEditModal = () => {
      showEditModal.value = false;
      selectedUser.value = null;
    };

    // Modal Functions - Assignment
    const openAssignmentModal = (user) => {
      selectedUser.value = user;
      showAssignmentModal.value = true;
    };

    const closeAssignmentModal = () => {
      showAssignmentModal.value = false;
      selectedUser.value = null;
    };

    // Modal Functions - Delete Confirm
    const openDeleteConfirm = (user) => {
      selectedUser.value = user;
      showDeleteConfirm.value = true;
    };

    const closeDeleteConfirm = () => {
      showDeleteConfirm.value = false;
      selectedUser.value = null;
    };

    // Modal Functions - Reset Password Confirm
    const openResetConfirm = (user) => {
      selectedUser.value = user;
      showResetConfirm.value = true;
    };

    const closeResetConfirm = () => {
      showResetConfirm.value = false;
      selectedUser.value = null;
    };

    // API Functions
    async function fetchGroupsForSite(siteId) {
      try {
        if (assignmentModalRef.value) {
          assignmentModalRef.value.setLoadingItems(true);
        }
        const data = await adminApi.getGroupsBySite(siteId);
        groupsList.value = data[1]?.groups || data || [];
      } catch (error) {
        console.error("Error fetching groups:", error);
        groupsList.value = [];
      } finally {
        if (assignmentModalRef.value) {
          assignmentModalRef.value.setLoadingItems(false);
        }
      }
    }

    // Legacy create flow — still used by GroupModal (SUPPLIER).
    // CustomerModal (P7 T7) now performs its own create + MCS-assign inline
    // and emits `created` instead; see handleUserCreated below.
    async function addCustomerFunc(customerData) {
      try {
        return await adminApi.createAdminAccount(customerData);
      } catch (error) {
        console.error("Error adding customer:", error);
        showToast("Failed to create account", "error");
      }
    }

    const saveCustomer = async (customer) => {
      await addCustomerFunc(customer);
      showToast("Account created successfully", "success");
    };

    // Handle @created emit from CustomerModal (P7 T7 replaced the legacy @save).
    // The modal performs both the create call and the MCS assignment in-place;
    // we just surface the outcome and close. The Firestore onSnapshot listener
    // above picks up the new Users/{uid} document automatically.
    const handleUserCreated = (event) => {
      closeCustomerModal();
      if (event?.mcs_assignment_failed) {
        showToast(
          "User created but MCS assignment failed. Open the user's assignment modal to retry.",
          "warning"
        );
      } else {
        showToast("User created successfully", "success");
      }
    };

    // Handle @created emit from PortalAdminModal. No MCS assignment step —
    // portal super admins carry no per-customer scope. Firestore onSnapshot
    // above picks up the new Users/{uid} document automatically.
    const handlePortalAdminCreated = () => {
      showPortalAdminModal.value = false;
      showToast("Portal admin created successfully", "success");
    };

    // Handle @saved emit from UserAssignmentModal's MCS branch (P7 T6).
    // The modal already called updateUserMcsCustomers itself; we just show the
    // confirmation + close. Firestore onSnapshot refreshes customers_mcs for
    // this user without a manual refetch.
    const handleMcsAssignmentsSaved = (event) => {
      closeAssignmentModal();
      const msg = event?.message || "Assignments saved.";
      showToast(msg, "success");
    };

    // Handle Update Profile
    const handleUpdateProfile = async (profileData) => {
      if (editModalRef.value) {
        editModalRef.value.setLoading(true);
      }

      try {
        await adminApi.updateUserProfile(profileData);
        showToast("Profile updated successfully", "success");
        closeEditModal();
      } catch (error) {
        console.error("Error updating profile:", error);
        if (editModalRef.value) {
          editModalRef.value.setError(error.message || "Failed to update profile");
        }
      } finally {
        if (editModalRef.value) {
          editModalRef.value.setLoading(false);
        }
      }
    };

    // Handle Update Customers
    const handleUpdateCustomers = async (data) => {
      if (assignmentModalRef.value) {
        assignmentModalRef.value.setLoading(true);
      }

      try {
        await adminApi.updateUserCustomers(data);
        showToast("Customer assignments updated successfully", "success");
        closeAssignmentModal();
      } catch (error) {
        console.error("Error updating customers:", error);
        if (assignmentModalRef.value) {
          assignmentModalRef.value.setError(
            error.message || "Failed to update customer assignments"
          );
        }
      } finally {
        if (assignmentModalRef.value) {
          assignmentModalRef.value.setLoading(false);
        }
      }
    };

    // Handle Update Groups
    const handleUpdateGroups = async (data) => {
      if (assignmentModalRef.value) {
        assignmentModalRef.value.setLoading(true);
      }

      try {
        await adminApi.updateUserGroups(data);
        showToast("Group assignments updated successfully", "success");
        closeAssignmentModal();
      } catch (error) {
        console.error("Error updating groups:", error);
        if (assignmentModalRef.value) {
          assignmentModalRef.value.setError(
            error.message || "Failed to update group assignments"
          );
        }
      } finally {
        if (assignmentModalRef.value) {
          assignmentModalRef.value.setLoading(false);
        }
      }
    };

    // Handle Reset Password
    const handleResetPassword = async () => {
      actionLoading.value = true;

      try {
        await adminApi.resetUserPassword(selectedUser.value.id);
        showToast(
          `Password reset email sent to ${selectedUser.value.email}`,
          "success"
        );
        closeResetConfirm();
      } catch (error) {
        console.error("Error resetting password:", error);
        showToast(error.message || "Failed to reset password", "error");
      } finally {
        actionLoading.value = false;
      }
    };

    // Handle Delete User
    const handleDeleteUser = async () => {
      actionLoading.value = true;

      try {
        await adminApi.deleteUser(selectedUser.value.id);
        showToast(
          `User ${selectedUser.value.first_name} ${selectedUser.value.last_name} deleted successfully`,
          "success"
        );
        closeDeleteConfirm();
      } catch (error) {
        console.error("Error deleting user:", error);
        showToast(error.message || "Failed to delete user", "error");
      } finally {
        actionLoading.value = false;
      }
    };

    // Initialize Data — customersList is no longer pre-populated (P7 T9).
    // CustomerModal / UserAssignmentModal / McsCustomersCatalog each fetch
    // their own data via AdminApiService; this ref stays as an empty array
    // only to satisfy the legacy `customersList` prop declarations in
    // CustomerModal and GroupModal, neither of which reads it anymore.

    // Fetch groups for default site (Dubai) — still needed for the
    // SUPPLIER branch of UserAssignmentModal, which surfaces groupsList
    // as `availableGroups`.
    fetchGroupsForSite("FA669BD0-42F8-4EDF-8847-9ACD191759F1");

    return {
      // Data
      users,
      filteredUsers,
      customersList,
      groupsList,
      searchQuery,
      accountTypeFilter,
      sortConfig,
      toast,
      roleLabel,

      // Modal States
      showCustomerModal,
      showGroupModal,
      showPortalAdminModal,
      showEditModal,
      showAssignmentModal,
      showDeleteConfirm,
      showResetConfirm,
      selectedUser,
      actionLoading,

      // Modal Refs
      editModalRef,
      assignmentModalRef,

      // Functions
      saveCustomer,
      addCustomer,
      closeCustomerModal,
      addGroup,
      closeGroupModal,
      sortBy,
      openEditModal,
      closeEditModal,
      openAssignmentModal,
      closeAssignmentModal,
      openDeleteConfirm,
      closeDeleteConfirm,
      openResetConfirm,
      closeResetConfirm,
      handleUpdateProfile,
      handleUpdateCustomers,
      handleUpdateGroups,
      handleUserCreated,
      handlePortalAdminCreated,
      handleMcsAssignmentsSaved,
      handleResetPassword,
      handleDeleteUser,
      fetchGroupsForSite,

      // Lucide icon symbols (template scope)
      Plus,
      Search,
      Pencil,
      UsersRound,
      KeyRound,
      Trash2,
      Users,
      SearchX,
      ShieldCheck,
    };
  }
};
</script>

<style scoped>
/* Toast animation */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fade-in {
  animation: fadeIn 0.3s ease-out;
}
</style>
